/**
 * CookiePreferencesModal — per-category opt-in toggles.
 *
 * Can be opened from:
 *   - The CookieConsentBanner's "Manage preferences" button.
 *   - Any "Cookie preferences" link (e.g. in the footer).
 *
 * Behaviour:
 *   - Opens with the current saved values as defaults.
 *   - "Save preferences" persists and closes.
 *   - "Accept all" / "Reject non-essential" are shortcut buttons.
 *   - Closing with Escape or backdrop click is a cancel (no save).
 *
 * Accessibility
 * ─────────────
 * - Each category row is a <section> with an <h3> heading describing the
 *   category, plus a <p> body. Both are referenced by the Switch via
 *   aria-labelledby / aria-describedby so AT users hear the category name
 *   and explanation when the switch receives focus.
 * - The intro paragraph and the policy link use semantic <p> / <a>; the
 *   inline link has a visible underline.
 */

import React, { useId } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, DialogActions, Switch } from '@mui/material';
import { Button } from '@codex/components/inputs';
import { useCookieConsent } from '../contexts/CookieConsentContext';

export default function CookiePreferencesModal(): JSX.Element {
  const { t } = useTranslation();
  const {
    categories,
    acceptAll,
    rejectNonEssential,
    setCategories,
    isPreferencesOpen,
    closePreferences,
  } = useCookieConsent();

  // Local state seeded from the persisted value — only written on Save.
  const [preferences, setPreferences] = React.useState(categories.preferences);
  const [analytics, setAnalytics] = React.useState(categories.analytics);

  // Re-seed when the modal opens (in case persisted value changed externally).
  React.useEffect(() => {
    if (isPreferencesOpen) {
      setPreferences(categories.preferences);
      setAnalytics(categories.analytics);
    }
  }, [isPreferencesOpen, categories.preferences, categories.analytics]);

  const handleSave = () => {
    setCategories({ preferences, analytics });
    closePreferences();
  };

  return (
    <Dialog
      open={isPreferencesOpen}
      onClose={closePreferences}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'var(--color-bg-elevated)',
          border: '1px solid var(--color-border-default)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: '0 24px 80px rgba(0, 0, 0, 0.3)',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: 18,
          fontWeight: 700,
          color: 'var(--color-text-primary)',
        }}
      >
        {t('cookies.modal.title', { defaultValue: 'Cookie preferences' })}
      </DialogTitle>

      <DialogContent>
        <p
          style={{
            fontSize: 14,
            lineHeight: 1.55,
            color: 'var(--color-text-secondary)',
            marginTop: 0,
            marginBottom: 20,
          }}
        >
          {t('cookies.modal.intro', {
            defaultValue:
              "Choose which cookies you allow. Strictly-necessary cookies are always on because they're required for sign-in and security. See our ",
          })}
          <Link
            to="/legal/cookies"
            style={{
              color: 'var(--color-text-link)',
              textDecoration: 'underline',
              textUnderlineOffset: 2,
              fontWeight: 500,
            }}
          >
            {t('cookies.modal.policyLink', { defaultValue: 'Cookie Policy' })}
          </Link>
          {t('cookies.modal.introTail', {
            defaultValue: ' for the full list.',
          })}
        </p>

        {/* ── Essential (always on) ──────────────────────────────────── */}
        <CategoryRow
          title={t('cookies.modal.essential.title', { defaultValue: 'Strictly necessary' })}
          body={t('cookies.modal.essential.body', {
            defaultValue:
              'Required for sign-in, CSRF protection, and core platform features. These cookies cannot be turned off.',
          })}
          checked
          disabled
        />

        {/* ── Preferences ─────────────────────────────────────────────── */}
        <CategoryRow
          title={t('cookies.modal.preferences.title', { defaultValue: 'Preferences' })}
          body={t('cookies.modal.preferences.body', {
            defaultValue:
              'Remember UI choices like theme, language, and reader font across visits.',
          })}
          checked={preferences}
          onChange={setPreferences}
        />

        {/* ── Analytics ───────────────────────────────────────────────── */}
        <CategoryRow
          title={t('cookies.modal.analytics.title', { defaultValue: 'Analytics' })}
          body={t('cookies.modal.analytics.body', {
            defaultValue:
              'Help us understand how the platform is used (PostHog). Anonymous visitors are counted in aggregate; only signed-in users are linked to a profile.',
          })}
          checked={analytics}
          onChange={setAnalytics}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1, flexWrap: 'wrap' }}>
        <Button variant="outlined" size="sm" onClick={rejectNonEssential}>
          {t('cookies.modal.reject', { defaultValue: 'Reject non-essential' })}
        </Button>
        <Button variant="outlined" size="sm" onClick={acceptAll}>
          {t('cookies.modal.acceptAll', { defaultValue: 'Accept all' })}
        </Button>
        <Button variant="filled" size="sm" onClick={handleSave}>
          {t('cookies.modal.save', { defaultValue: 'Save preferences' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Row ───────────────────────────────────────────────────────────────────────

interface CategoryRowProps {
  title: string;
  body: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (next: boolean) => void;
}

/**
 * Each category is a <section> containing an <h3> heading + <p> body. The
 * Switch's input is wired to both via aria-labelledby (the title) and
 * aria-describedby (the body) so AT users hear the category name AND the
 * explanation when the switch receives focus.
 */
function CategoryRow({ title, body, checked, disabled, onChange }: CategoryRowProps) {
  const titleId = useId();
  const bodyId = useId();

  return (
    <section
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'flex-start',
        gap: 16,
        padding: '12px 0',
        borderTop: '1px solid var(--color-border-default)',
      }}
    >
      <div>
        <h3
          id={titleId}
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--color-text-primary)',
            margin: '0 0 4px 0',
          }}
        >
          {title}
        </h3>
        <p
          id={bodyId}
          style={{
            fontSize: 13,
            lineHeight: 1.5,
            color: 'var(--color-text-secondary)',
            margin: 0,
          }}
        >
          {body}
        </p>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        inputProps={{
          'aria-labelledby': titleId,
          'aria-describedby': bodyId,
        }}
        sx={{ m: 0 }}
      />
    </section>
  );
}
